import { useState } from "react";
import Modal from "./Modal";
import { Users, UserPlus } from "lucide-react";
import { DEFAULT_YEAR } from "../../config/constants";

export const CreateReportModal = ({
  isOpen,
  onClose,
  onSubmit,
  availableTeams,
  selectedDivision,
  onDivisionChange,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [selectedYear, setSelectedYear] = useState(String(DEFAULT_YEAR));
  const [formError, setFormError] = useState("");
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [addAllPlayers, setAddAllPlayers] = useState(false);

  const handleSubmit = () => {
    if (!selectedTeamId) {
      setFormError("Please select a team");
      return;
    }

    const reportData = {
      teamId: selectedTeamId,
      teamName: selectedTeamName,
      division: selectedDivision,
      year: Number(selectedYear),
      addAllPlayers,
    };

    onSubmit(reportData);
    setFormError("");
    setAddAllPlayers(false);
  };

  const handleClose = () => {
    setSelectedTeamId("");
    setSelectedTeamName("");
    setFormError("");
    setAddAllPlayers(false);
    onClose();
  };

  const handleDivisionChange = async (newDivision) => {
    setIsLoadingTeams(true);
    setSelectedTeamId("");
    setSelectedTeamName("");
    await onDivisionChange(newDivision, Number(selectedYear));
    setIsLoadingTeams(false);
  };

  const handleYearChange = async (newYear) => {
    setSelectedYear(newYear);
    setIsLoadingTeams(true);
    setSelectedTeamId("");
    setSelectedTeamName("");
    await onDivisionChange(selectedDivision, Number(newYear));
    setIsLoadingTeams(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Scouting Report">
      <div className="space-y-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Division
          </label>
          <select
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            disabled={isLoadingTeams}
          >
            <option value="ncaa_1">Division 1</option>
            <option value="ncaa_2">Division 2</option>
            <option value="ncaa_3">Division 3</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Season Year
          </label>
          <select
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            disabled={isLoadingTeams}
          >
            {Array.from({ length: DEFAULT_YEAR - 2020 }, (_, i) => String(DEFAULT_YEAR - i)).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Team
          </label>
          <select
            value={selectedTeamId}
            onChange={(e) => {
              const teamId = e.target.value;
              const team = availableTeams.find((t) => String(t.team_id) === teamId);
              setSelectedTeamId(teamId);
              setSelectedTeamName(team?.team_name || "");
              setFormError("");
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            disabled={isLoadingTeams}
          >
            <option value="">Choose a team...</option>
            {availableTeams.map((team) => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}
              </option>
            ))}
          </select>
          {isLoadingTeams && (
            <p className="mt-1 text-sm text-blue-600">Loading teams...</p>
          )}
          {formError && (
            <p className="mt-1 text-sm text-red-600">{formError}</p>
          )}
        </div>

        {selectedTeamId && (
          <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={addAllPlayers}
                onChange={(e) => setAddAllPlayers(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">Add all players automatically</span>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Import all batters and pitchers from {selectedTeamName}
                </p>
              </div>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedTeamId || isLoadingTeams}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {addAllPlayers && <UserPlus className="w-4 h-4" />}
            {addAllPlayers ? "Create & Add All Players" : "Create Report"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
